import React from 'react';
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '../../context/ThemeContext';

export interface SuccessButtonProps extends Omit<ButtonProps, 'variant'> {
  loading?: boolean;
  icon?: React.ReactNode;
}

const SuccessButton: React.FC<SuccessButtonProps> = ({
  children,
  loading = false,
  disabled,
  icon,
  sx,
  ...props
}) => {
  const { theme } = useTheme();
  const btn = theme.button.success;

  // Fallback to top-level success color if button.success tokens missing
  const bg = btn?.bg ?? theme.success;
  const text = btn?.text ?? '#FFFFFF';
  const hover = btn?.hover ?? theme.successHover;

  return (
    <Button
      variant="contained"
      disabled={disabled || loading}
      startIcon={!loading ? icon : undefined}
      sx={{
        minHeight: 44,
        backgroundColor: bg,
        color: text,
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '6px',
        '&:hover': {
          backgroundColor: hover,
        },
        '&:active': {
          backgroundColor: btn?.pressed ?? hover,
        },
        '&.Mui-disabled': {
          backgroundColor: btn?.disabled ?? theme.gray[200],
          color: theme.gray[400],
        },
        ...sx,
      }}
      {...props}
    >
      {loading ? (
        <CircularProgress size={20} color="inherit" sx={{ mr: children ? 1 : 0 }} />
      ) : null}
      {children}
    </Button>
  );
};

export default SuccessButton;
